import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import JobListTable from "../../components/Table/JobListTable";
import api from "../../../utils/axios";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";

export default function JobListPage({
  contentOnly = false,
  branchFilter = "all",
  onBranchFilterChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "pending", "assigned"
  const [teamFilter, setTeamFilter] = useState("all"); // "all", "1", "2", "3"
  const [startDate, setStartDate] = useState(""); // Date range filter start
  const [endDate, setEndDate] = useState(""); // Date range filter end
  const [serviceDateMode, setServiceDateMode] = useState("all"); // "all" | "day" | "month" | "year"

  const toYmdLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const applyServiceDateMode = (mode) => {
    const now = new Date();
    if (mode === "all") {
      setStartDate("");
      setEndDate("");
      return;
    }
    if (mode === "day") {
      const today = toYmdLocal(now);
      setStartDate(today);
      setEndDate(today);
      return;
    }
    if (mode === "month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(toYmdLocal(start));
      setEndDate(toYmdLocal(end));
      return;
    }
    // year (default)
    const start = new Date(now.getFullYear(), 0, 1);
    const end = new Date(now.getFullYear(), 11, 31);
    setStartDate(toYmdLocal(start));
    setEndDate(toYmdLocal(end));
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const compId = localStorage.getItem("company");
      const response = await api.get("/get-all-jobs-with-teams", {
        params: compId ? { comp_id: compId } : {}
      });

      if (response.data?.status === "OK") {
        let rows = response.data.result || [];

        // ใช้สถานะจาก endpoint เดียวกับ tab ปฏิทิน (assigned + current) เพื่อให้สถานะในตารางตรงกัน
        try {
          const startDate = new Date(new Date().getFullYear(), 0, 1, 0, 0, 0);
          const endDate = new Date(new Date().getFullYear(), 11, 31, 23, 59, 59);
          const toYmd = (d) => d.toISOString().slice(0, 10);
          const [assignedRes, currentRes] = await Promise.all([
            api.get("/get-assigned-jobs", {
              params: {
                ...(compId ? { comp_id: compId } : {}),
                start_date: toYmd(startDate),
                end_date: toYmd(endDate),
              },
            }),
            api.get("/get-current-jobs", {
              params: compId ? { comp_id: compId } : {},
            }),
          ]);

          const statusByRoundId = new Map();
          const statusByRoundNo = new Map();
          const teamByRoundId = new Map();
          const teamByRoundNo = new Map();
          const pushStatus = (r) => {
            const status = String(r?.jobStatus || r?.Job_Status || "").trim();
            const roundIdKey = String(r?.Round_Id ?? "");
            const roundNoKey = `${String(r?.JobId ?? "")}__${String(r?.Round_No ?? "")}`;
            if (status) {
              if (roundIdKey) statusByRoundId.set(roundIdKey, status);
              if (roundNoKey !== "__") statusByRoundNo.set(roundNoKey, status);
            }
            const teamId = r?.teamId ?? r?.Team_Id ?? r?.TeamId ?? null;
            const teamName = String(r?.teamName || r?.TeamName || "").trim();
            if (teamId != null || teamName) {
              const teamPayload = { TeamId: teamId, TeamName: teamName };
              if (roundIdKey) teamByRoundId.set(roundIdKey, teamPayload);
              if (roundNoKey !== "__") teamByRoundNo.set(roundNoKey, teamPayload);
            }
          };
          if (assignedRes.data?.status === "OK" && Array.isArray(assignedRes.data.result)) {
            assignedRes.data.result.forEach(pushStatus);
          }
          if (currentRes.data?.status === "OK" && Array.isArray(currentRes.data.result)) {
            currentRes.data.result.forEach(pushStatus);
          }

          rows = rows.map((row) => {
            const rowRoundIdKey = String(row.Round_Id ?? "");
            const rowRoundNoKey = `${String(row.JobId ?? "")}__${String(row.Round_No ?? "")}`;
            const byRoundId = statusByRoundId.get(rowRoundIdKey);
            const byRoundNo = statusByRoundNo.get(rowRoundNoKey);
            const mappedStatus = byRoundId || byRoundNo;
            const mappedTeam = teamByRoundId.get(rowRoundIdKey) || teamByRoundNo.get(rowRoundNoKey);
            const nextRow = { ...row };
            if (mappedStatus) nextRow.Job_Status = mappedStatus;
            if (mappedTeam) {
              if ((nextRow.TeamId == null || nextRow.TeamId === "") && mappedTeam.TeamId != null) {
                nextRow.TeamId = mappedTeam.TeamId;
              }
              if ((!nextRow.TeamName || String(nextRow.TeamName).trim() === "") && mappedTeam.TeamName) {
                nextRow.TeamName = mappedTeam.TeamName;
              }
            }
            return nextRow;
          });
        } catch (syncErr) {
          console.warn("sync status from calendar failed:", syncErr);
        }

        setJobs(rows);
      } else {
        Swal.fire({
          icon: "error",
          title: "เกิดข้อผิดพลาด",
          text: response.data.message || "ไม่สามารถดึงข้อมูลได้",
        });
        setJobs([]);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: "ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้",
      });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };


  const listContent = (
    <>
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
          <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      ) : (
        <JobListTable
          fullHeight={contentOnly}
          jobs={jobs}
          onView={(row, navMeta) => navigate(`/jobfile/${row.JobId}`, {
            state: {
              job: row,
              backLink: navMeta?.backLink || `${location.pathname}${location.search || ""}`,
              backLabel: navMeta?.backLabel || "ตารางงานทั้งหมด",
            },
          })}
          onEdit={(row, navMeta) => navigate(`/jobfile/${row.JobId}`, {
            state: {
              job: row,
              backLink: navMeta?.backLink || `${location.pathname}${location.search || ""}`,
              backLabel: navMeta?.backLabel || "ตารางงานทั้งหมด",
            },
          })}
          onDelete={(row) => {
            Swal.fire({
              title: "ยืนยันการลบ",
              text: `คุณต้องการลบงาน ${row.Contract_No || row.JobId} ใช่หรือไม่?`,
              icon: "warning",
              showCancelButton: true,
              confirmButtonColor: "#d33",
              cancelButtonColor: "#3085d6",
              confirmButtonText: "ลบ",
              cancelButtonText: "ยกเลิก",
            }).then((result) => {
              if (result.isConfirmed) {
                // TODO: Implement delete API call
                Swal.fire("ลบแล้ว", "ลบงานสำเร็จ", "success");
                fetchJobs();
              }
            });
          }}
          statusFilter={statusFilter}
          teamFilter={teamFilter}
          onStatusFilterChange={setStatusFilter}
          onTeamFilterChange={setTeamFilter}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          serviceDateMode={serviceDateMode}
          onServiceDateModeChange={(mode) => {
            setServiceDateMode(mode);
            applyServiceDateMode(mode);
          }}
          branchFilter={branchFilter}
          onBranchFilterChange={onBranchFilterChange}
        />
      )}
    </>
  );

  if (contentOnly) {
    return (
      <div className="h-full min-h-0 pl-2 pr-8 pb-8">
        <div className="h-full min-h-0">{listContent}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      
      <main
        className="flex-1 h-screen overflow-auto bg-gray-50 transition-all duration-300"
        style={{ marginLeft: "var(--sidebar-width)" }}
      >
        <Topbar
          breadcrumbs={[
            { label: "จัดการจ๊อบงาน", link: "/job-management" },
            { label: "ตารางงานทั้งหมด" },
          ]}
        />

        <div className="pl-2 pr-8 pt-6 pb-2">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Icon icon="mdi:table" className="w-6 h-6 text-[#E13F57]" />
              ตารางงานทั้งหมด
            </h1>

            <button
              onClick={() => navigate("/job-management")}
              className="flex items-center gap-1 text-[#F17F25] text-sm font-bold border-b-2 border-[#F17F25]"
            >
              <Icon icon="mdi:arrow-left" className="w-5 h-5" />
              กลับ
            </button>
          </div>
        </div>

        <div className="pl-2 pr-8 pb-8">
          {listContent}
        </div>
      </main>
    </div>
  );
}

